/**
 * @file responsiveProps.ts
 *
 * @fileoverview Helper methods to allow for returning the right class names for responsive properties
 * based on media query values (sm, md, lg).
 */

import { jobiqoCSSModuleClasses } from "../../types/cssModules";

// Since I can't read this from the css this has to be duplicated.
// This would normally be in a centralized place.
export const sizes = ["sm", "md", "lg", "xl"];

type PropertyType = any | any[] | undefined;

function isPropertyFalsy(property: PropertyType): boolean {
  return property !== 0 && !property;
}

function getClassesInitialArray(property?: PropertyType): PropertyType[] {
  return Array.isArray(property) ? property : [property];
}

/**
 * Returns a list of classnames to add to an element. Each one will respond to the right
 * name of the property set in that stylesheet.
 *
 * @param {jobiqoCSSModuleClasses} styles
 *   The list of styles for the component being used.
 *
 * @param property
 *   The prop that was passed to the component (e.g. ["row", "column"] for an array of responsive props for flex direction).
 *
 * @param {string} classProperty
 *   The class property name to attach (e.g. "fd" for flex-direction).
 *
 * @returns {string}
 *   The string list of classnames to attach to "className".
 */
export const getScopedClasses = (
  styles: jobiqoCSSModuleClasses,
  classProperty: string,
  property: PropertyType
): string => {
  if (isPropertyFalsy(property)) return "";

  return getClassesInitialArray(property)
    .map((property, index) => {
      return styles[`${classProperty}-${sizes[index]}-${property}`];
    })
    .join(" ");
};

/**
 * Returns a list of classnames to add to an element. Each one will respond to the right
 * name of the property set in that stylesheet.
 *
 * @param property
 *   The prop that was passed to the component (e.g. ["row", "column"] for an array of responsive props for flex direction).
 *
 * @param {string} classProperty
 *   The class property name to attach (e.g. "fd" for flex-direction).
 *
 * @returns {string}
 *   The string list of classnames to attach to "className".
 */
export const getGlobalClasses = (
  property: PropertyType,
  classProperty?: string
): string => {
  if (isPropertyFalsy(property)) return "";

  return getClassesInitialArray(property)
    .map((property, index) => {
      return `${classProperty}-${sizes[index]}-${property}`;
    })
    .join(" ");
};

/**
 * Returns a list of classnames to add to an element. Each one will respond to the right
 * name of the property set in that stylesheet. If the styles parameter is null, the
 * applied styles will be scoped to the passed argument module, and global otherwise.
 *
 * @param {jobiqoCSSModuleClasses} styles
 *   The list of styles for the component being used.
 *
 * @param property
 *   The prop that was passed to the component (e.g. ["row", "column"] for an array of responsive props for flex direction).
 *
 * @param {string} classProperty
 *   The class property name to attach (e.g. "fd" for flex-direction).
 *
 * @returns {string}
 *   The string list of classnames to attach to "className".
 */
export const getClasses = (
  styles: jobiqoCSSModuleClasses | null,
  classProperty: string,
  property?: any | any[]
): string => {
  return styles
    ? getScopedClasses(styles, classProperty, property)
    : getGlobalClasses(property, classProperty);
};
